import React, { lazy, Suspense } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useInView } from 'react-intersection-observer';
import Title from 'components/UI/Title';
import Loader from 'components/UI/Loader';
import LazyImage from 'components/UI/LazyImage';
import Steps from './Steps';
import { useTrans } from 'utils/localisation';
import { getImagesWithArtDirection } from 'utils/images';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './Intro.module.scss';

const CorporateForm = lazy(() => import('components/Forms/CorporateForm/CorporateForm'));

function Intro({
  formId = 'get-consultation',
  title,
  stepsTitle,
  steps,
  commentPlaceholder,
  commentPlaceholderMessage,
  initialIndustry,
  submitTitle,
  gatsbyImage,
  imgAlt = '',
}) {
  const t = useTrans(['common', 'getConsultation']);
  const images = getImagesWithArtDirection(gatsbyImage);
  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  return (
    <section id="intro" className={styles.headerPlaceholder}>
      <div className={styles.container}>
        <GatsbyImage
          alt={imgAlt}
          image={images}
          className={styles.picture}
          imgClassName={styles.pictureImage}
          loading="eager"
        />
        <div className={styles.wrapper}>
          <div className={styles.column}>
            <Title tag="h1" size="h1Secondary" className={styles.title} color="white">
              {title ?? t('contactUsTitle')}
            </Title>
            <Steps
              title={stepsTitle ?? t('processStepsTitle')}
              steps={steps ?? t('processSteps')}
              className={styles.steps}
            />
            <div className={styles.clientsList}>
              <LazyImage
                src="https://static.andersenlab.com/andersenlab/new-andersensite/icons/clutch-logo-rating.svg"
                alt="Clutch"
                className={styles.clientLogo}
              />
              <LazyImage
                src="https://static.andersenlab.com/andersenlab/new-andersensite/icons/goodfirms-logo-rating.svg"
                alt="Goodfirms"
                className={styles.clientLogo}
              />
            </div>
          </div>
          <div className={styles.column}>
            <div className={styles.mobileTitles}>
              <Title tag="h1" size="h1Secondary" className={styles.title} color="white">
                {title ?? t('contactUsTitle')}
              </Title>
            </div>
            <div ref={ref} className={styles.formWrap}>
              {inView && (
                <Suspense fallback={<Loader theme="fullHeight" />}>
                  <CorporateForm
                    id={formId}
                    className={styles.form}
                    submitTitle={submitTitle ?? t('sendRequest')}
                    formName="get-consultation"
                    initialIndustry={initialIndustry}
                    commentPlaceholder={commentPlaceholder}
                    commentPlaceholderMessage={commentPlaceholderMessage}
                  />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

Intro.displayName = 'Intro';

export default withErrorBoundary(Intro);
